/**
 * Billing Thunks
 * Async thunks for billing management operations
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  cancelSubscription as cancelSubscriptionService,
  createBillingPortalSession as createBillingPortalSessionService,
  fetchAllPlanRateLimits,
  fetchCompanyBilling,
  fetchCompanyInvoices,
  fetchSubscriptionPackages,
  fetchUsageStats,
  resumeSubscription as resumeSubscriptionService,
  updateSubscriptionPackage as updateSubscriptionPackageService,
} from '../../services/billingService';
import {
  BillingCycle,
  BillingRecord,
  Invoice,
  PlanRateLimit,
  SubscriptionPackageDetails,
  UsageStats,
} from '../../types/billing';

interface FetchUsageParams {
  companyId: number;
  packageType: string;
}

export const fetchBilling = createAsyncThunk<
  BillingRecord | null,
  number,
  { rejectValue: string }
>('billing/fetchBilling', async (companyId, { rejectWithValue }) => {
  try {
    const response = await fetchCompanyBilling(companyId);
    if (!response.success) {
      return rejectWithValue(response.error || 'Failed to fetch billing');
    }
    return response.data || null;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to fetch billing',
    );
  }
});

export const fetchPackages = createAsyncThunk<
  SubscriptionPackageDetails[],
  void,
  { rejectValue: string }
>('billing/fetchPackages', async (_, { rejectWithValue }) => {
  try {
    const response = await fetchSubscriptionPackages();
    if (!response.success) {
      return rejectWithValue(response.error || 'Failed to fetch packages');
    }
    return response.data || [];
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to fetch packages',
    );
  }
});

export const fetchInvoices = createAsyncThunk<
  Invoice[],
  number,
  { rejectValue: string }
>('billing/fetchInvoices', async (companyId, { rejectWithValue }) => {
  try {
    const response = await fetchCompanyInvoices(companyId);
    if (!response.success) {
      return rejectWithValue(response.error || 'Failed to fetch invoices');
    }
    return response.data || [];
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to fetch invoices',
    );
  }
});

export const fetchUsage = createAsyncThunk<
  UsageStats,
  FetchUsageParams,
  { rejectValue: string }
>(
  'billing/fetchUsage',
  async ({ companyId, packageType }, { rejectWithValue }) => {
    try {
      const response = await fetchUsageStats(companyId, packageType);
      if (!response.success || !response.data) {
        return rejectWithValue(response.error || 'Failed to fetch usage stats');
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch usage stats',
      );
    }
  },
);

export const fetchRateLimits = createAsyncThunk<
  Record<string, PlanRateLimit>,
  void,
  { rejectValue: string }
>('billing/fetchRateLimits', async (_, { rejectWithValue }) => {
  try {
    const response = await fetchAllPlanRateLimits();
    if (!response.success || !response.data) {
      return rejectWithValue(
        response.error || 'Failed to fetch plan rate limits',
      );
    }
    return response.data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error
        ? error.message
        : 'Failed to fetch plan rate limits',
    );
  }
});

export const createBillingPortalSession = createAsyncThunk<
  { url: string },
  string,
  { rejectValue: string }
>(
  'billing/createBillingPortalSession',
  async (returnUrl, { rejectWithValue }) => {
    try {
      const response = await createBillingPortalSessionService(returnUrl);
      if (!response.success || !response.data) {
        return rejectWithValue(
          response.error || 'Failed to create billing portal session',
        );
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to create billing portal session',
      );
    }
  },
);

export const cancelSubscription = createAsyncThunk<
  void,
  boolean,
  { rejectValue: string }
>(
  'billing/cancelSubscription',
  async (cancelAtPeriodEnd, { rejectWithValue }) => {
    try {
      const response = await cancelSubscriptionService(cancelAtPeriodEnd);
      if (!response.success) {
        return rejectWithValue(
          response.error || 'Failed to cancel subscription',
        );
      }
      return undefined;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to cancel subscription',
      );
    }
  },
);

export const resumeSubscription = createAsyncThunk<
  void,
  void,
  { rejectValue: string }
>('billing/resumeSubscription', async (_, { rejectWithValue }) => {
  try {
    const response = await resumeSubscriptionService();
    if (!response.success) {
      return rejectWithValue(response.error || 'Failed to resume subscription');
    }
    return undefined;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to resume subscription',
    );
  }
});

export const updateSubscriptionPackage = createAsyncThunk<
  void,
  { packageId: string; billingCycle?: BillingCycle },
  { rejectValue: string }
>(
  'billing/updateSubscriptionPackage',
  async ({ packageId, billingCycle }, { rejectWithValue }) => {
    try {
      const response = await updateSubscriptionPackageService(
        packageId,
        billingCycle,
      );
      if (!response.success) {
        return rejectWithValue(
          response.error || 'Failed to update subscription',
        );
      }
      return undefined;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to update subscription',
      );
    }
  },
);
